import React from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, Legend
} from "recharts";

const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const tooltipStyle = {
  contentStyle: { background: "#0d0d0d", border: "1px solid #00ff9033", borderRadius: 8, fontSize: 11 },
  labelStyle: { color: "#00ff90" },
};

export default function TrendsPanel({ loading, data }) {
  const labels     = data?.labels     || [];
  const compliance = data?.compliance || [];
  const anomalies  = data?.anomalies  || [];
  const wfh        = data?.wfh        || [];

  // Build combined dataset
  const chartData = labels.map((label, i) => ({
    label,
    compliance: compliance[i] ?? null,
    anomalies:  anomalies[i]  ?? null,
    wfh:        wfh[i]        ?? null,
  }));

  if (loading) return (
    <div className={`${card} p-5 lg:col-span-2`}>
      <div className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-4">Attendance Trends</div>
      <div className="h-48 rounded-lg bg-white/5 animate-pulse" />
    </div>
  );

  return (
    <div className={`${card} lg:col-span-2`}>
      <div className="px-5 py-4 border-b border-white/5">
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Attendance Trends</span>
      </div>

      <div className="p-5">
        {chartData.length === 0 ? (
          <p className="text-white/20 text-sm italic text-center py-8">No trend data available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Compliance % line chart */}
            <div>
              <p className="text-xs text-white/30 uppercase tracking-wider mb-3">Compliance % (Monthly)</p>
              <ResponsiveContainer width="100%" height={140}>
                <LineChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} unit="%" />
                  <Tooltip {...tooltipStyle} formatter={v => [`${v}%`, "Compliance"]} />
                  <Line type="monotone" dataKey="compliance" stroke="#00ff90" strokeWidth={2}
                    dot={{ fill: "#00ff90", r: 3 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Anomalies bar chart */}
            <div>
              <p className="text-xs text-white/30 uppercase tracking-wider mb-3">Anomaly Count (Monthly)</p>
              <ResponsiveContainer width="100%" height={140}>
                <BarChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
                  <YAxis tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} allowDecimals={false} />
                  <Tooltip {...tooltipStyle} formatter={v => [v, "Anomalies"]} />
                  <Bar dataKey="anomalies" fill="#f87171" fillOpacity={0.7} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* WFH % if available */}
            {wfh.length > 0 && (
              <div className="md:col-span-2">
                <p className="text-xs text-white/30 uppercase tracking-wider mb-3">WFH % Trend</p>
                <ResponsiveContainer width="100%" height={100}>
                  <LineChart data={chartData} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: "rgba(255,255,255,0.3)" }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 9, fill: "rgba(255,255,255,0.25)" }} unit="%" />
                    <Tooltip {...tooltipStyle} formatter={v => [`${v}%`, "WFH"]} />
                    <Line type="monotone" dataKey="wfh" stroke="#c084fc" strokeWidth={2} dot={{ fill: "#c084fc", r: 3 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}